<template>
  <div class="bg-white p-4 space-y-8 border rounded-lg">
    <div class="flex">
      <div class="ml-auto w-1/3">
        <div class="space-y-2">
          <div class="select-wrapper space-y-2">
            <select id="period" v-model="selectedPeriod" class="select" placeholder="Select period">
              <option v-for="(per, index) in periods" :value="index" :key="index">
                {{ per.since }} - {{ per.until }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <div class="flex">
      <div class="flex-none space-y-4 pr-8">
        <label class="label custom-checkbox">
          <input
            type="checkbox"
            @input="displayImpressions = $event.target.checked"
            :checked="displayImpressions"
          />
          <span />
          <span class="text-indigo-500">
            {{ $t('impressions') | capitalize }}
          </span>
        </label>
        <label class="label custom-checkbox">
          <input
            type="checkbox"
            @input="displayReach = $event.target.checked"
            :checked="displayReach"
          />
          <span />
          <span class="text-indigo-200">
            {{ $t('reach') | capitalize }}
          </span>
        </label>
        <label class="label custom-checkbox">
          <input
            type="checkbox"
            @input="displayInlineLinkClicks = $event.target.checked"
            :checked="displayInlineLinkClicks"
          />
          <span />
          <span class="text-yellow-500">
            {{ $t('visits') | capitalize }}
          </span>
        </label>
        <label class="label custom-checkbox">
          <input
            type="checkbox"
            @input="displayConversions = $event.target.checked"
            :checked="displayConversions"
          />
          <span />
          <span class="text-teal-500">
            {{ $t('conversions') | capitalize }}
          </span>
        </label>
        <label class="label custom-checkbox">
          <input
            type="checkbox"
            @input="displaySpend = $event.target.checked"
            :checked="displaySpend"
          />
          <span />
          <span class="text-red-500">
            {{ $t('spend') | capitalize }}
          </span>
        </label>
        <label class="label custom-checkbox">
          <input
            type="checkbox"
            @input="displayCostPerClick = $event.target.checked"
            :checked="displayCostPerClick"
          />
          <span />
          <span class="text-red-200">
            {{ $t('costPerClick') | capitalize }}
          </span>
        </label>
        <label class="label custom-checkbox">
          <input
            type="checkbox"
            @input="displayCostPerReach = $event.target.checked"
            :checked="displayCostPerReach"
          />
          <span />
          <span class="text-orange-500">
            {{ $t('costPerReach') | capitalize }}
          </span>
        </label>
      </div>
      <div class="flex-1 card bg-gray-200 border-none p-4">
        <div class="relative">
          <slot :chart-data="chartData" :options="options" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InsightsGraph',
  props: {
    dailyReports: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    selectedPeriod: 0,
    displayImpressions: true,
    displayReach: true,
    displayInlineLinkClicks: true,
    displayConversions: true,
    displaySpend: false,
    displayCostPerClick: false,
    displayCostPerReach: false,
    options: {
      responsive: true,
      aspectRatio: 3,
      layout: {
        padding: {
          top: 10,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            maxTicksLimit: 5,
          },
        },
        y: {
          display: false,
        },
      },
      elements: {
        line: {
          tension: 0,
          borderCapStyle: 'round',
          spanGaps: false,
        },
        point: {
          radius: 0,
        },
      },
      tooltips: {
        callbacks: {
          label(tooltipItem, data) {
            const { datasetIndex, index } = tooltipItem;
            const dataset = data.datasets[datasetIndex];
            const { originalData, dataSuffix } = dataset;
            return `${originalData[index]}${dataSuffix}`;
          },
        },
      },
    },
  }),
  computed: {
    sortedAsc() {
      return this.dailyReports.slice(0).sort((reportA, reportB) => {
        const [yearA, monthA, dayA] = reportA.dateFrom.split('-');
        const dateA = new Date(yearA, monthA - 1, dayA, 0, 0, 0);

        const [yearB, monthB, dayB] = reportB.dateFrom.split('-');
        const dateB = new Date(yearB, monthB - 1, dayB, 0, 0, 0);

        return dateA - dateB;
      });
    },
    minDate() {
      const [firstInsight] = this.sortedAsc.slice(0, 1);
      return firstInsight ? firstInsight.dateFrom || null : null;
    },
    maxDate() {
      const [lastInsight] = this.sortedAsc.slice(-1);
      return lastInsight ? lastInsight.dateFrom || null : null;
    },
    groupsOfConsecutiveDates() {
      const dates = this.dailyReports.map((dailyReport) => dailyReport.dateFrom);
      const groupByPeriod = [];
      let prevDate = null;
      let increment = 0;
      groupByPeriod.push([]);
      dates.forEach((date) => {
        const current = this.parseDate(date);
        if (prevDate && Math.round((current - prevDate) / (1000 * 60 * 60 * 24)) > 1) {
          increment += 1;
          groupByPeriod.push([]);
        }
        const year = current.getFullYear();
        const month = `0${current.getMonth() + 1}`.slice(-2);
        const day = `0${current.getDate()}`.slice(-2);
        groupByPeriod[increment].push(`${year}-${month}-${day}`);
        prevDate = new Date(current.getTime());
      });
      return groupByPeriod;
    },
    consecutiveDateRange() {
      const labels = [];
      const { minDate, maxDate } = this;
      if (!minDate) return [];
      const firstDate = this.parseDate(minDate);
      const lastDate = this.parseDate(maxDate);
      for (let date = firstDate; date <= lastDate; date.setUTCDate(date.getUTCDate() + 1)) {
        const year = date.getFullYear();
        const month = `0${date.getMonth() + 1}`.slice(-2);
        const day = `0${date.getDate()}`.slice(-2);
        labels.push(`${year}-${month}-${day}`);
      }
      return labels;
    },
    periods() {
      const periods = [];
      let [since] = this.consecutiveDateRange;
      let [until] = this.consecutiveDateRange.slice(-1);
      periods.push({
        since,
        until,
        value: this.consecutiveDateRange,
      });

      if (this.groupsOfConsecutiveDates.length > 1) {
        this.groupsOfConsecutiveDates.forEach((group) => {
          [since] = group;
          [until] = group.slice(-1);
          periods.push({
            since,
            until,
            value: [...group],
          });
        });
      }
      return periods;
    },
    period() {
      return this.periods[this.selectedPeriod] || null;
    },
    impressionsByCurrentPeriod() {
      const { period } = this;
      if (!period || !period.value) return [];
      return period.value.map((label) => {
        const report = this.dailyReports.find((dailyReport) => dailyReport.dateFrom === label);
        const { impressions = null } = report || {};
        return impressions;
      });
    },
    reachByCurrentPeriod() {
      const { period } = this;
      if (!period || !period.value) return [];
      return period.value.map((label) => {
        const report = this.dailyReports.find((dailyReport) => dailyReport.dateFrom === label);
        const { reach = null } = report || {};
        return reach;
      });
    },
    inlineLinkClicksByCurrentPeriod() {
      const { period } = this;
      if (!period || !period.value) return [];
      return period.value.map((label) => {
        const report = this.dailyReports.find((dailyReport) => dailyReport.dateFrom === label);
        const { inlineLinkClicks = null } = report || {};
        return inlineLinkClicks;
      });
    },
    conversionsByCurrentPeriod() {
      const { period } = this;
      if (!period || !period.value) return [];
      return period.value.map((label) => {
        const report = this.dailyReports.find((dailyReport) => dailyReport.dateFrom === label);
        const { inlineLinkClicks = null, reach = null } = report || {};
        if (!reach || !inlineLinkClicks) return null;
        return ((inlineLinkClicks / reach) * 100).toFixed(2);
      });
    },
    spendByCurrentPeriod() {
      const { period } = this;
      if (!period || !period.value) return [];
      return period.value.map((label) => {
        const report = this.dailyReports.find((dailyReport) => dailyReport.dateFrom === label);
        const { spend = null } = report || {};
        return spend;
      });
    },
    costPerClickByCurrentPeriod() {
      const { period } = this;
      if (!period || !period.value) return [];
      return period.value.map((label) => {
        const report = this.dailyReports.find((dailyReport) => dailyReport.dateFrom === label);
        const { spend = null, inlineLinkClicks = null } = report || {};
        if (!spend || !inlineLinkClicks) return null;
        return (spend / inlineLinkClicks).toFixed(2);
      });
    },
    costPerReachByCurrentPeriod() {
      const { period } = this;
      if (!period || !period.value) return [];
      return period.value.map((label) => {
        const report = this.dailyReports.find((dailyReport) => dailyReport.dateFrom === label);
        const { spend = null, reach = null } = report || {};
        if (!spend || !reach) return null;
        return (spend / reach).toFixed(4);
      });
    },
    chartData() {
      const datasets = [];

      const common = {
        backgroundColor: 'transparent',
        borderWidth: 4,
        pointHitRadius: 10,
        pointHoverRadius: 6,
      };

      if (this.displayImpressions) {
        datasets.push({
          label: 'impressions',
          originalData: this.impressionsByCurrentPeriod,
          dataSuffix: '',
          data: [...this.normalizeData(this.impressionsByCurrentPeriod)],
          ...common,
          borderColor: '#6574cd',
          pointBackgroundColor: '#6574cd',
        });
      }

      if (this.displayReach) {
        datasets.push({
          label: 'reach',
          originalData: this.reachByCurrentPeriod,
          dataSuffix: '',
          data: [
            ...this.normalizeData(
              this.reachByCurrentPeriod,
              this.displayImpressions ? this.impressionsByCurrentPeriod : []
            ),
          ],
          ...common,
          borderColor: '#b2b7ff',
          borderWidth: 2,
          borderDash: [5, 4],
          pointBackgroundColor: '#b2b7ff',
        });
      }

      if (this.displayInlineLinkClicks) {
        let additionalNormalizeData = [];
        if (this.displayImpressions) {
          additionalNormalizeData = this.impressionsByCurrentPeriod;
        } else if (this.displayReach) {
          additionalNormalizeData = this.reachByCurrentPeriod;
        }
        datasets.push({
          label: 'clicks',
          originalData: this.inlineLinkClicksByCurrentPeriod,
          dataSuffix: '',
          data: [
            ...this.normalizeData(this.inlineLinkClicksByCurrentPeriod, additionalNormalizeData),
          ],
          ...common,
          borderColor: '#ffed4a',
          pointBackgroundColor: '#ffed4a',
        });
      }

      if (this.displayConversions) {
        datasets.push({
          label: 'conversions',
          originalData: this.conversionsByCurrentPeriod,
          dataSuffix: '',
          data: [...this.normalizeData(this.conversionsByCurrentPeriod, [0, 1])],
          ...common,
          borderColor: '#4dc0b5',
          pointBackgroundColor: '#4dc0b5',
        });
      }

      if (this.displaySpend) {
        datasets.push({
          label: 'spend',
          originalData: this.spendByCurrentPeriod,
          dataSuffix: '\u20AC',
          data: [...this.normalizeData(this.spendByCurrentPeriod)],
          ...common,
          borderColor: '#e3342f',
          pointBackgroundColor: '#e3342f',
        });
      }

      if (this.displayCostPerClick) {
        datasets.push({
          label: 'cpc',
          originalData: this.costPerClickByCurrentPeriod,
          dataSuffix: '\u20AC',
          data: [
            ...this.normalizeData(
              this.costPerClickByCurrentPeriod,
              this.displaySpend ? this.spendByCurrentPeriod : []
            ),
          ],
          ...common,
          borderColor: '#f9acaa',
          borderWidth: 2,
          borderDash: [5, 4],
          pointBackgroundColor: '#f9acaa',
        });
      }

      if (this.displayCostPerReach) {
        datasets.push({
          label: 'cpc',
          originalData: this.costPerReachByCurrentPeriod,
          dataSuffix: '\u20AC',
          data: [
            ...this.normalizeData(this.costPerReachByCurrentPeriod, [
              ...(this.displaySpend ? this.spendByCurrentPeriod : []),
              ...(!this.displaySpend && this.displayCostPerClick ? this.spendByCurrentPeriod : []),
            ]),
          ],
          ...common,
          borderColor: '#f6993f',
          borderWidth: 2,
          borderDash: [5, 4],
          pointBackgroundColor: '#f6993f',
        });
      }

      return {
        datasets,
        labels: this.period.value,
      };
    },
  },
  methods: {
    parseDate(value) {
      const [year, month, day] = value.split('-');
      return new Date(year, month - 1, day, 0, 0, 0);
    },
    normalizeData(values, additionalValues = []) {
      const sampleData = [...values, ...additionalValues];
      const sortedAsc = sampleData.sort((a, b) => a - b);
      const [min] = sortedAsc;
      const [max] = sortedAsc.slice(-1);
      return values.map((value) => {
        if (!value) return null;
        return (value - min) / (max - min);
      });
    },
  },
};
</script>
